/**
 * SRNavbar Component
 * ==================
 * Dynamic navbar - ALL content from Blueprint
 * Fallback to direct Supabase query and defaults
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBlueprint } from '../../context/BlueprintContext';
import { useAuth } from '../../App';
import { Menu, X, ChevronDown } from 'lucide-react';
import supabase from '../../lib/supabase';

const SRNavbar = () => {
  const { navbar, lang, changeLanguage, getNavLinks, getRoute } = useBlueprint();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmsLinks, setCmsLinks] = useState([]);
  
  // Handle scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Fetch nav links from CMS if Blueprint context is empty
  useEffect(() => {
    const fetchCmsLinks = async () => {
      if (getNavLinks().length > 0) return; // Blueprint has data
      
      try {
        // Try RPC first
        const { data: rpcData, error } = await supabase.rpc('get_blueprint_config', { p_lang: 'it' });
        
        if (!error && rpcData?.navbar?.links?.length > 0) {
          console.log('SRNavbar: Using Blueprint RPC links');
          setCmsLinks(rpcData.navbar.links.map(l => ({
            text: l.title,
            route: `/${l.slug}`,
            requiresAuth: l.requires_auth
          })));
          return;
        }
        
        // Fallback to direct query
        const { data: pagesData } = await supabase
          .from('blueprint_pages')
          .select('slug, title, title_it, requires_auth')
          .eq('show_in_top_menu', true)
          .eq('is_published', true)
          .order('top_menu_order', { ascending: true });
        
        if (pagesData?.length > 0) {
          console.log('SRNavbar: Using direct CMS query');
          setCmsLinks(pagesData.map(p => ({
            text: p.title_it || p.title,
            route: `/${p.slug}`,
            requiresAuth: p.requires_auth
          })));
        }
      } catch (err) {
        console.error('SRNavbar: Error fetching CMS links:', err);
      }
    };
    
    fetchCmsLinks();
  }, [getNavLinks]);
  
  // Get content from Blueprint
  const logo = navbar?.logo || {};
  const auth = navbar?.auth || {};
  
  // Safe text extraction helper - handles strings, objects, and nested values
  const safeText = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      // Handle { text, route } format
      if (value.text) return safeText(value.text, fallback);
      if (value.label) return safeText(value.label, fallback);
      // Last resort - stringify
      console.warn('⚠️ Navbar: Unexpected object value:', value);
      return fallback;
    }
    return fallback;
  };
  
  const loginText = safeText(auth?.login, 'Login');
  const dashboardText = safeText(auth?.dashboard, 'Dashboard');
  const logoMark = safeText(logo?.mark, 'SR');
  const logoName = safeText(logo?.name, 'SLABREALITY');
  const logoTagline = safeText(logo?.tagline, '');
  
  // Get nav links from config (config-driven routing)
  const navLinks = getNavLinks();
  // Use CMS links if available, then Blueprint config, then navbar.links
  let links = cmsLinks.length > 0 ? cmsLinks : (navLinks.length > 0 ? navLinks : (navbar?.links || []));
  
  // If still empty, use default navigation
  if (links.length === 0) {
    links = [
      { text: 'Materiali', route: '/materials' },
      { text: 'Ricerca Visiva', route: '/visual-search' },
      { text: 'DNA Compare', route: '/dna-compare' },
      { text: 'Progetti', route: '/projects', requiresAuth: true }
    ];
  }
  
  // Get routes from config
  const homeRoute = getRoute('home', '/');
  const loginRoute = getRoute('login', '/login');
  const dashboardRoute = getRoute('dashboard', '/dashboard');
  
  return (
    <nav className={`sr-navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="sr-container">
        <div className="flex items-center justify-between h-[68px]">
          {/* Logo - uses config route */}
          <Link to={homeRoute} className="flex items-center gap-3">
            <div className="w-[38px] h-[38px] border-[1.5px] border-[var(--dark)] flex items-center justify-center font-[var(--ff-h)] text-sm font-semibold text-[var(--dark)]">
              {logoMark}
            </div>
            <div>
              <div className="text-[11.5px] font-bold tracking-[3.5px] text-[var(--dark)] leading-tight">
                {logoName}
              </div>
              <div className="text-[7.5px] tracking-[1.2px] text-[#AAA]">
                {logoTagline}
              </div>
            </div>
          </Link>
          
          {/* Desktop Nav Links - config-driven routes */}
          <div className="hidden lg:flex items-center gap-7">
            {links.map((link, i) => {
              // Handle both formats: { text, url } or { text, route }
              const linkText = safeText(link.text || link.label, link.key || `Link ${i}`);
              const linkUrl = link.url || link.route || '/';
              
              return (
                <Link
                  key={link.key || i}
                  to={linkUrl}
                  className="text-[13.5px] font-medium text-[#222] hover:text-[var(--gold)] transition-colors"
                >
                  {linkText}
                </Link>
              );
            })}
          </div>
          
          {/* Right Side */}
          <div className="flex items-center gap-3.5">
            {/* Auth Button - Desktop - uses config routes */}
            <button
              onClick={() => navigate(isAuthenticated ? dashboardRoute : loginRoute)}
              className="hidden lg:block sr-btn-login"
            >
              {isAuthenticated ? dashboardText : loginText}
            </button>
            
            {/* Language Selector */}
            <button className="flex items-center gap-0.5 text-xs text-[#666] font-medium cursor-pointer">
              {lang.toUpperCase()}
              <ChevronDown size={13} />
            </button>
            
            {/* Hamburger - Mobile */}
            <button
              className="lg:hidden p-1 text-[var(--dark)]"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden flex flex-col gap-4 px-8 py-5 border-t border-[var(--border)] bg-white">
          {links.map((link, i) => {
            // Handle both formats: { text, url } or { text, route }
            const linkText = safeText(link.text || link.label, link.key || `Link ${i}`);
            const linkUrl = link.url || link.route || '/';
            
            return (
              <Link
                key={link.key || i}
                to={linkUrl}
                className="text-[15px] text-[#333]"
                onClick={() => setMobileOpen(false)}
              >
                {linkText}
              </Link>
            );
          })}
          <button
            onClick={() => {
              navigate(isAuthenticated ? dashboardRoute : loginRoute);
              setMobileOpen(false);
            }}
            className="sr-btn-login w-fit"
          >
            {isAuthenticated ? dashboardText : loginText}
          </button>
        </div>
      )}
    </nav>
  );
};

export default SRNavbar;
